import { Eyebrow, MaskLines, Reveal } from "./Reveal";

const TYPES = [
  {
    id: "tpo",
    title: "Low-Slope TPO Roofs",
    text: "Fire-rated TPO membrane systems for flat and low-slope roofs, laid over laminated polyiso insulation board for a warmer, quieter interior. Seams are heat-welded, parapets and flashings are detailed clean, and the white surface reflects the New Mexico sun. Well installed and maintained, a TPO roof will last 25–30 years.",
    tags: ["Fire-rated", "Polyiso insulation", "25–30 year lifespan"],
    img: "/images/tpo-main.jpg",
    img2: "/images/tpo-1.jpg",
    img3: "/images/tpo-2.jpg",
    ratio: "600/400",
    alt: "White fire-rated TPO roof with skylights on a New Mexico home",
  },
  {
    id: "shingle",
    title: "Shingle Roofs",
    text: "Pitched-roof tear-offs, replacements and repairs using Owens Corning shingles — skylight installs included — installed to spec, from underlayment to ridge cap. Expect 15–30 years depending on the type of shingle, installation quality and weather exposure.",
    tags: ["Owens Corning Preferred", "Skylights", "15–30 year lifespan"],
    img: "/images/oc-shingles.jpg",
    img2: "/images/shingle-3.jpg",
    img3: "/images/shingle-5.jpg",
    img4: "/images/skylight-1.jpg",
    ratio: "1438/900",
    alt: "Pitched roof with Owens Corning shingles",
  },
  {
    id: "metal",
    title: "Metal Roofs",
    text: "Standing-seam metal roofs for homes, cabins and outbuildings — panels installed with clean edges, tight flashings and a durable finish. Depending on the metal, coating, installation quality and maintenance, a metal roof can last 40–70 years.",
    tags: ["Standing seam", "Panels", "40–70 year lifespan"],
    img: "/images/metal-1.jpg",
    img2: "/images/metal-2.jpg",
    img3: "/images/shingle-2.jpg",
    ratio: "1300/853",
    wide: true,
    alt: "Standing-seam metal roof on a timber home",
  },
  {
    id: "soffit",
    title: "Metal Soffits & Fascia",
    text: "Vented metal soffit panels and metal fascia that finish the roofline — steady attic airflow at the eaves, with trim that won't rot, warp or need repainting.",
    tags: ["Vented panels", "Metal fascia", "Low maintenance"],
    img: "/images/soffit-1.jpg",
    img2: "/images/soffit-2.jpg",
    ratio: "1320/619",
    wide: true,
    alt: "Perforated metal soffit and metal fascia along a metal roof edge",
  },
  {
    id: "conversion",
    title: "Flat-to-Pitch Conversions",
    text: "We reframe flat roofs as pitched ones — new trusses framed and sheathed over the old roof so it sheds water and snow instead of pooling it. Shown here mid-conversion.",
    tags: ["New trusses", "Framing & sheathing", "Re-roof"],
    img: "/images/flat-to-pitch.jpg",
    ratio: "2000/1500",
    wide: true,
    alt: "A flat-roofed home reframed with new pitched roof trusses",
  },
];

const span = (t) => (t.wide ? "md:col-span-2" : "");

export const Roofing = () => (
  <section id="roofing" data-testid="roofing-section" className="bg-[#090B0E] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
      <div className="mb-14 grid gap-6 lg:grid-cols-2 lg:items-end">
        <div>
          <Eyebrow>Roofing</Eyebrow>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-6xl lg:text-7xl">
            <MaskLines inView lines={["Flat or pitched.", <span key="d" className="text-blue-500">Done right.</span>]} />
          </h2>
        </div>
        <Reveal>
          <p className="max-w-md text-base leading-relaxed text-slate-300 lg:ml-auto sm:text-lg">
            We install and repair the roof types you see across Northern New Mexico — low-slope TPO, pitched shingle and standing-seam metal — plus the metal soffits and fascia that finish the roofline. Every roof gets dry-in materials that meet or exceed building code requirements.
          </p>
        </Reveal>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {TYPES.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.1} className={span(t)}>
            <article data-testid={`roof-type-${t.id}`} className="clip-corner group relative overflow-hidden bg-[#12161F]">
              <div className="grid gap-px bg-white/10" style={{ gridTemplateColumns: t.img5 ? "repeat(6, 1fr)" : t.img4 ? "1fr 1fr" : t.img3 ? "1fr 1fr 1fr" : t.img2 ? "1fr 1fr" : "1fr" }}>
                {[t.img, t.img2, t.img3, t.img4, t.img5].filter(Boolean).map((src, i) => (
                  <img key={src} src={src} alt={i === 0 ? t.alt : `${t.alt} — detail ${i + 1}`} loading="lazy" className={`w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105 ${i === 0 ? "block" : "hidden sm:block"}`} style={{ aspectRatio: t.img5 ? "4/3" : t.ratio, gridColumn: t.img5 ? (i < 3 ? "span 2" : "span 3") : undefined, objectPosition: i === 0 ? (t.id === "metal" ? "left center" : "center") : "center" }} />
                ))}
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-display text-3xl font-bold uppercase text-white sm:text-4xl">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">{t.text}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {t.tags.map((tag) => (
                    <span key={tag} className="border border-white/15 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-slate-300">{tag}</span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
